import BaseSubscription from './BaseSubscription';
import { logError } from './services';
import iconLogoWhite from './../../public/assets/images/icon-logo-white.png';
import { showAlert } from '../components/UI';


export const PWA_INSTALLED = 'PWA_INSTALLED';
export const PWA_CAN_BE_INSTALLED = 'PWA_CAN_BE_INSTALLED';
export const PWA_REGISTERED_SERVICE_WORKER = 'PWA_REGISTERED_SERVICE_WORKER';


export default class PWA extends BaseSubscription {

  /**
   * File src/workers/serviceWorker.js is NOT bundled with other JavaScript into the main bundle
   * Instead, it's simply watched for changes and copied into ./dist so that it's accessible at http://hostname/serviceWorker.js
   *
   * @see src/workers/serviceWorker.js
   */
  SERVICE_WORKER_PATH = '/serviceWorker.js';

  serviceWorkerRegistration = null;
  deferredPrompt = null;
  canInstall = false;

  constructor(){
    super();

    if ('serviceWorker' in navigator) {
      this.registerServiceWorker().then(this.onServiceWorkerRegistered);
    }
    window.addEventListener('beforeinstallprompt', this.passDeferredPrompt);
    window.addEventListener('appinstalled', () => {
      this.triggerEvent(PWA_INSTALLED, null, { firedOnce: true });
      location.reload(true); // without refreshing PWA badge is not updating
    });
  }

  registerServiceWorker = () => {
    return new Promise((resolve, reject) => {
      try {
        /**
         * When registering a service worker, save the path into a VARIABLE first and then
         * pass this VARIABLE into navigator.serviceWorker.register(VARIABLE)
         *
         * Otherwise, if passing a string path directly, e.g. navigator.serviceWorker.register('/my-service-worker.js'),
         * Parcel Bundler would rebuild 'my-service-worker.js' file and would add invalid Parcel JS-code in there
         * (e.g. 'window' object that is not supported inside the service worker)
         * @see https://github.com/parcel-bundler/parcel/issues/331#issuecomment-562257024
         */
        navigator.serviceWorker.register(this.SERVICE_WORKER_PATH, { scope: '/' })
          .then(resolve)
          .catch(error => {
            const errorMessage = 'PWA: Unable to register Service Worker';
            showAlert({ text: errorMessage, dontAutoHide: true, type: 'error' });
            logError(errorMessage, { error });
            reject(error);
          });
      }
      catch (error) {
        const errorMessage = 'PWA: Error while registering Service Worker';
        showAlert({ text: errorMessage, dontAutoHide: true, type: 'error' });
        logError(errorMessage, { error });
        reject(error);
      }
    });
  };

  onServiceWorkerRegistered = (serviceWorkerRegistration) => {
    this.serviceWorkerRegistration = serviceWorkerRegistration;
    const { controller } = navigator.serviceWorker;
    if (controller) {
      controller.addEventListener('error', error => {
        const errorMessage = 'PWA: ServiceWorker error: ' + error.message;
        showAlert({ text: errorMessage, dontAutoHide: true, type: 'error' });
        logError(errorMessage, { error });
      });
    }
    this.triggerEvent(PWA_REGISTERED_SERVICE_WORKER, this.serviceWorkerRegistration, { firedOnce: true });
  };

  passDeferredPrompt = (deferredPrompt) => {
    this.deferredPrompt = deferredPrompt;
    if (deferredPrompt) {
      this.canInstall = true;
      this.triggerEvent(PWA_CAN_BE_INSTALLED, this.deferredPrompt, { firedOnce: true });
    }
  };

  installApp = () => {
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();
    }
  };

  setBadgeCounter = (count) => {
    if (navigator.setAppBadge) {
      navigator.setAppBadge(count);
    }
  };

  renderIOSSplashScreen = () => {
    const { availWidth, availHeight } = screen;
    const backgroundColor = '#0033FF'; // $color-blue in src/styles/variables.scss

    const canvas = document.createElement('canvas');
    canvas.width = availWidth * devicePixelRatio;
    canvas.height = availHeight * devicePixelRatio;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = backgroundColor;
    ctx.scale(devicePixelRatio, devicePixelRatio);
    ctx.fillRect(0, 0, availWidth, availHeight);

    const icon = new Image();
    icon.onload = () => {
      let canvasIconWidth = availWidth * 0.4;
      let canvasIconHeight = (icon.height / icon.width) * availWidth * 0.4;
      if (availWidth > availHeight) {
        canvasIconHeight = availHeight * 0.3;
        canvasIconWidth = (icon.width / icon.height) * availHeight * 0.3;
      }
      let canvasIconX = (availWidth - canvasIconWidth) / 2;
      let canvasIconY = (availHeight - canvasIconHeight) / 2;

      ctx.drawImage(icon, canvasIconX, canvasIconY, canvasIconWidth, canvasIconHeight);
      document.querySelector('link[rel=apple-touch-startup-image]').href = canvas.toDataURL('image/png');
    };
    icon.src = iconLogoWhite;
  };

  isRunningStandalone = () => {
    const isStandaloneIOS = Boolean(navigator.standalone);
    let isStandaloneChrome = false;
    try {
      isStandaloneChrome = Boolean(window.matchMedia('(display-mode: standalone)').matches); // Chrome
    }
    catch (e) {}
    return isStandaloneIOS || isStandaloneChrome;
  };

  unregisterServiceWorkers = async () => {
    return new Promise((async (resolve, reject) => {
      if (!navigator.serviceWorker) {
        resolve();
        return;
      }
      const serviceWorkerRegistrations = await navigator.serviceWorker.getRegistrations();
      for (let i = 0; i < serviceWorkerRegistrations.length; i++) {
        const registration = serviceWorkerRegistrations[i];
        try {
          await registration.unregister();
        }
        catch (e) {
          reject(e);
          return;
        }
      }
      resolve();
    }));
  };

  reset = () => {
    if (window.caches) {
      window.caches.keys().then(keys => {
        keys.forEach(key => caches.delete(key));
      });
    }
    for (let key in localStorage) {
      if (key !== 'TOKEN') {
        localStorage.removeItem(key);
      }
    }
    return this.unregisterServiceWorkers();
  }
}
